// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SignInMessage } from "@awc/Overrides/Widgets/Header/AddressDrawerFulfillmentMethodSelector";
import { color } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import {
    getFulfillmentLabel,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import MapPin from "@insite/mobius/Icons/MapPin";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const signInPageLink = getPageLinkByPageType(state, "SignInPage")?.url;
    return {
        fulfillmentLabel: getFulfillmentLabel(state),
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        showSignInMessage:
            getSession(state).fulfillmentMethod === FulfillmentMethod.Ship &&
            !getSession(state).isAuthenticated &&
            signInPageLink,
        signInPageLink,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    // XNG-Changes
    modal?: ModalPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
            &:hover {
                background-color: ${color("common.ui15")};
            }
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
            width: calc(100% - 10px);
            padding: 6px 0;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentLabel,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    // XNG-Changes
    showSignInMessage,
    signInPageLink,
    history,
}) => {
    // XNG-Changes
    const [isModalOpen, setIsModalOpen] = useState(false);
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        // setDrawerIsOpen({ isOpen: !isDrawerOpen });
        setIsModalOpen(true);
    };

    // XNG-Changes
    // const handleCloseDrawer = () => {
    //     setDrawerIsOpen({ isOpen: false });
    // };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    // XNG-Changes
    const handleSignInMessageClick = () => {
        if (!signInPageLink) {
            return;
        }

        setIsModalOpen(false);
        history.push(signInPageLink);
    };

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <Clickable
                    {...styles.titleClickable}
                    onClick={handleClickAddress}
                    data-test-selector="header_shipToButton"
                    aria-label={translate("header shipToButton")}
                >
                    {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                    <Icon
                        {...styles.titleIcon}
                        src={MapPin}
                        // XNG-change
                        aria-hidden="true"
                    />
                    <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                        {fulfillmentLabel}
                    </Typography>
                </Clickable>
            </StyledWrapper>
            {/* XNG-Changes */}
            <Modal
                {...styles.modal}
                headline={translate("Fulfillment Method")}
                isOpen={isModalOpen}
                handleClose={() => {
                    setIsModalOpen(false);
                }}
                data-test-selector="modal_FulfillmentMethod"
            >
                {createWidgetElement("ChangeCustomer/ChangeCustomerFulfillmentMethodSelector", {
                    fields: {},
                    hideLabel: true,
                } as any)}
                {showSignInMessage && <SignInMessage onClick={handleSignInMessageClick} />}
            </Modal>
            {/* <Drawer
                {...drawerStyles}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
            </Drawer> */}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withIsInShell(HeaderShipToAddress))),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
